import {
  addOneMinute,
  filterOperatorValues,
  getMinutesFromTime,
  subtractOneMinute,
} from '../../../utils'
import { DateTimeFilterPropsType } from '../../../utils/types/tableFilterTypes'
import { isEmpty } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { SvgCross, SvgFunnel } from '../../../assets/images'

export const DateTimeFilterInput = <T,>({
  columnData,
  handleFilterChange,
  dataQaId,
  filterObject,
  filterOperator,
  columnHeaderRef,
  innerInputRef,
  tabIndex,
}: DateTimeFilterPropsType<T>) => {
  const search_key =
    columnData?.filterData?.convertToUppercase === false
      ? (columnData?.accessor as string)
      : (columnData?.accessor as string)?.toUpperCase()

  const [inputValue, setInputValue] = useState<string>()
  const [inputToValue, setInputToValue] = useState<string>()
  const [isFilterApplied, setIsFilterApplied] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const inputToRef = useRef<HTMLInputElement | null>(null)

  const getTimeInUTC = (time: string): string => {
    const currentDateString = new Date().toISOString().split('T')[0]
    const fullDateTime = `${currentDateString}T${time}Z`
    const date = new Date(fullDateTime)
    const timezoneOffset = date.getTimezoneOffset()
    return new Date(date.getTime() + timezoneOffset * 60000)
      .toISOString()
      .split('.')[0]
      .split('T')[1]
  }

  const isRangeOperator = () => {
    return (
      filterOperator?.value === filterOperatorValues.BETWEEN ||
      filterOperator?.value === filterOperatorValues.NOT_BETWEEN
    )
  }

  const isNullOrNotNull = () => {
    return (
      filterOperator?.value === filterOperatorValues.IS_NULL ||
      filterOperator?.value === filterOperatorValues.IS_NOT_NULL
    )
  }

  const onFilterChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e?.key === 'Enter') {
      ;(e.target as HTMLInputElement).blur()
    }
  }

  const getDynamicDate = (val: string) => {
    let time = moment(new Date(val).toISOString())
    // Add one minute for isBeforeOrEqualTo as input only takes values upto minutes
    // but actual values are upto mili seconds
    if (
      filterOperator?.value === filterOperatorValues.IS_BEFORE_OR_EQUAL_TO ||
      filterOperator?.value === filterOperatorValues.IS_AFTER
    )
      time = time.add(1, 'minutes').subtract(1, 'seconds')
    return time.utc().format('YYYY-MM-DDTHH:mm:ssZ')
  }

  const handleFocusOut = () => {
    const searchInput: string[] = []
    let changeRangeOperator = false
    if (
      isRangeOperator() &&
      inputToRef?.current?.value &&
      inputRef?.current?.value &&
      columnData?.filterData?.dataType === 'time'
    ) {
      if (
        getMinutesFromTime(inputRef.current.value) >
        getMinutesFromTime(inputToRef.current.value)
      )
        return
      if (
        getMinutesFromTime(
          getTimeInUTC(`${inputRef?.current?.value}:00`).substring(0, 5)
        ) >
        getMinutesFromTime(
          getTimeInUTC(`${inputToRef?.current?.value}:00`).substring(0, 5)
        )
      ) {
        changeRangeOperator = true
      }
    }
    if (!isEmpty(inputRef?.current?.value)) {
      let selectedDate = ''
      if (columnData?.filterData?.dataType === 'date-time') {
        selectedDate = moment(inputRef?.current?.value)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      } else if (columnData?.filterData?.dataType === 'date-time-dynamic') {
        selectedDate = getDynamicDate(inputRef.current!.value)
      } else {
        selectedDate = getTimeInUTC(`${inputRef?.current?.value}:00`)
      }
      searchInput.push(selectedDate)
    }
    if (isRangeOperator() && inputToRef?.current?.value) {
      let otherSelectedDate = ''
      if (columnData?.filterData?.dataType === 'date-time') {
        otherSelectedDate = moment(inputToRef?.current?.value)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      } else if (columnData?.filterData?.dataType === 'date-time-dynamic') {
        otherSelectedDate = moment(
          inputToRef.current?.value &&
            new Date(inputToRef.current.value).toISOString()
        )
          .utc()
          .format('YYYY-MM-DDTHH:mm:ssZ')
      } else {
        otherSelectedDate = getTimeInUTC(`${inputToRef?.current?.value}:00`)
      }

      searchInput.push(otherSelectedDate)
    }
    if (
      (!filterObject ||
        !search_key ||
        filterObject?.search_criteria?.[search_key]?.search_value?.[0] !==
          searchInput[0] ||
        filterObject?.search_criteria?.[search_key]?.search_value?.[1] !==
          searchInput[1] ||
        filterOperator?.value !==
          filterObject?.search_criteria?.[search_key]?.operator) &&
      (!isRangeOperator() || searchInput.length !== 1)
    ) {
      if (
        isRangeOperator() &&
        searchInput.length &&
        moment(searchInput[1]).isBefore(moment(searchInput[0]))
      ) {
        ;[searchInput[0], searchInput[1]] = [searchInput[1], searchInput[0]]
      }
      if (changeRangeOperator) {
        handleFilterChange({
          search_key: search_key,
          operator:
            filterOperator?.value === filterOperatorValues.BETWEEN
              ? filterOperatorValues.NOT_BETWEEN
              : filterOperatorValues.BETWEEN,
          search_value: [
            addOneMinute(searchInput[1]),
            subtractOneMinute(searchInput[0]),
          ],
        })
      } else {
        handleFilterChange({
          search_key: search_key,
          operator: filterOperator?.value ?? '',
          search_value: searchInput,
        })
      }
    }
  }
  const handleClearFilterButtonClick = (index?: number) => {
    if (index === 1 && inputRef?.current?.value) {
      inputRef.current.value = ''
      setInputValue(inputRef?.current?.value)
    } else if (inputToRef?.current?.value) {
      inputToRef.current.value = ''
      setInputToValue(inputToRef?.current?.value)
    }

    handleFocusOut()
  }

  const handleFilterValueChange = (index?: number) => {
    if (index === 1) {
      setInputValue(inputRef?.current?.value)
    } else {
      setInputToValue(inputToRef?.current?.value)
    }
  }

  useEffect(() => {
    if (isNullOrNotNull()) {
      if (inputRef.current) {
        inputRef.current.disabled = true
        inputRef.current.value = ''
        setInputValue('')
      }
      handleFilterChange({
        search_key: search_key,
        operator: filterOperator?.value ?? '',
        search_value: [''],
      })
    } else if (inputRef.current) inputRef.current.disabled = false
  }, [filterOperator])

  useEffect(() => {
    if (
      filterObject &&
      search_key &&
      filterObject?.search_criteria?.[search_key]
    ) {
      setIsFilterApplied(true)
    } else setIsFilterApplied(false)
  }, [filterObject])

  useEffect(() => {
    innerInputRef.current = inputRef.current
  }, [])

  useEffect(() => {
    const raw1 = filterObject?.search_criteria?.[search_key]?.search_value?.[0]
    const raw2 = filterObject?.search_criteria?.[search_key]?.search_value?.[1]

    const toInputValue = (val?: string) => {
      if (!val) return ''
      if (columnData?.filterData?.dataType === 'time') {
        return val.slice(0, 5)
      }
      if (columnData?.filterData?.dataType === 'date-time') {
        return moment.utc(val).local().format('YYYY-MM-DD[T]HH:mm')
      }
      return moment(val).local().format('YYYY-MM-DD[T]HH:mm')
    }

    setInputValue(toInputValue(raw1))
    setInputToValue(toInputValue(raw2))
  }, [filterObject])

  useEffect(() => {
    console.log(inputValue, 'inputValue')
  }, [inputValue])

  return (
    <div
      className="flex-1 flex items-center"
      onBlur={(e) => {
        if (
          !e.relatedTarget ||
          !columnHeaderRef.current?.contains(e.relatedTarget)
        )
          handleFocusOut()
      }}
    >
      <div className="relative flex-1 flex items-center">
        <div className="absolute left-2">
          <SvgFunnel
            width={'16'}
            height={'16'}
            className={`cursor-default ${
              isFilterApplied
                ? 'svg-path-fill-secondary'
                : 'svg-path-fill-accent-300'
            }
            `}
          />
        </div>
        <input
          data-qa-id={`${dataQaId}_input_1`}
          max="9999-12-31T23:59"
          type={
            columnData?.filterData?.dataType === 'time'
              ? 'time'
              : `datetime-local`
          }
          className={`w-full border border-border-5 rounded-md focus:outline-none focus:border-secondary-500 hover:border-secondary-500 disabled:hover:border-border-5 text-xs font-light p-2 pl-7 min-w-60`}
          ref={inputRef}
          onKeyDown={onFilterChange}
          value={inputValue}
          onChange={() => handleFilterValueChange(1)}
          tabIndex={tabIndex}
        />
        {filterOperator?.value === filterOperatorValues.IS_NULL ||
        filterOperator?.value === filterOperatorValues.IS_NOT_NULL ||
        inputValue ? (
          <div className="absolute right-[1.9rem] px-1 pb-0.5">
            <SvgCross
              width={'10'}
              height={'10'}
              className={'svg-path-fill-secondary focus:outline-none'}
              onClick={() => {
                handleClearFilterButtonClick(1)
              }}
              tabIndex={-1}
            />
          </div>
        ) : null}
      </div>
      {isRangeOperator() ? (
        <>
          <div className="p-1 font-light">-</div>
          <div className="relative flex-1 flex items-center">
            <div className="absolute left-2">
              <SvgFunnel
                width={'16'}
                height={'16'}
                className={`cursor-default ${
                  isFilterApplied
                    ? 'svg-path-fill-secondary'
                    : 'svg-path-fill-accent-300'
                }
                `}
              />
            </div>

            <input
              data-qa-id={`${dataQaId}_input_2`}
              type={
                columnData?.filterData?.dataType === 'time'
                  ? 'time'
                  : `datetime-local`
              }
              className={`w-full border border-border-5 rounded-md focus:outline-none focus:border-secondary-500 hover:border-secondary-500 disabled:hover:border-border-5 text-xs font-light p-2 pl-7`}
              ref={inputToRef}
              onKeyDown={onFilterChange}
              value={inputToValue}
              onChange={() => handleFilterValueChange(2)}
            />
            {filterOperator?.value === filterOperatorValues.IS_NULL ||
            filterOperator?.value === filterOperatorValues.IS_NOT_NULL ||
            inputToValue ? (
              <div className="absolute right-[1.9rem] px-1 pb-0.5">
                <SvgCross
                  width={'10'}
                  height={'10'}
                  className={'svg-path-fill-secondary focus:outline-none'}
                  onClick={() => {
                    handleClearFilterButtonClick(2)
                  }}
                  tabIndex={-1}
                />
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  )
}
